import React, { useState } from "react";
import { apiConfig } from "../firebase-config";
import { useReturnContext } from "../context/ReturnContext";
import "./SubmitRequest.css";

export default function SubmitRequest({
  order,
  items,
  returnType,
  notes,
  onRequestSubmitted,
  onBack,
}) {
  const {
    orderData: contextOrderData,
    selectedItems: contextSelectedItems,
    returnType: contextReturnType,
    notes: contextNotes,
  } = useReturnContext();

  // Use context values if props are not provided (for direct navigation)
  const effectiveOrder = order || contextOrderData;
  const effectiveItems =
    items || contextSelectedItems.filter((item) => item.selected);
  const effectiveReturnType = returnType || contextReturnType;
  const effectiveNotes = notes || contextNotes;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!effectiveOrder || !effectiveItems || effectiveItems.length === 0) {
      setError("Invalid request data. Please go back and try again.");
      return;
    }

    setLoading(true);
    setError("");

    // Create the return/exchange request object
    const requestData = {
      orderId: effectiveOrder.id,
      orderNumber: effectiveOrder.name ?? "",
      customerEmail: effectiveOrder.email || "",
      customerName: effectiveOrder.customer
        ? `${effectiveOrder.customer.first_name || ""} ${
            effectiveOrder.customer.last_name || ""
          }`.trim()
        : "N/A",
      items: effectiveItems.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: parseFloat(item.price).toFixed(2),
        variant_id: item.variant_id || "",
        product_id: item.product_id || "",
        sku: item.sku || "",
      })),
      returnType: effectiveReturnType,
      notes: effectiveNotes,
      orderDate: effectiveOrder.created_at || new Date().toISOString(),
    };

    try {
      // Call Firebase function directly instead of using Next.js API
      const functionUrl = `${apiConfig.baseUrl}${apiConfig.endpoints.handleReturnRequest}`;

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to submit request");
      }

      console.log("Return request saved with ID:", result.requestId);

      // Call the onRequestSubmitted callback
      onRequestSubmitted();
    } catch (err) {
      console.error("Error submitting return request:", err);

      // Check for specific Firebase errors
      if (err.message && err.message.includes("Firestore is not enabled")) {
        setError(
          "Database not configured: Firestore is not enabled in the Firebase project. Please contact the administrator."
        );
      } else if (
        err.message &&
        err.message.includes("Firebase Admin SDK is not properly configured")
      ) {
        setError(
          "Firebase Admin SDK is not properly configured. Please contact the administrator."
        );
      } else {
        setError("Failed to submit your request. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="submit-request">
      <h2 className="section-title">Review & Submit</h2>
      <p className="mb-6 text-gray-600">
        Please review your return or exchange request before submitting.
      </p>

      <div className="card">
        <div className="card-body">
          <div className="summary-section">
            <h3>Order Details</h3>
            <div className="summary-item">
              <span className="label">Order Number:</span>
              <span className="value">{effectiveOrder.name}</span>
            </div>
            <div className="summary-item">
              <span className="label">Date:</span>
              <span className="value">
                {new Date(effectiveOrder.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="summary-section">
            <h3>Items</h3>
            <ul className="item-list">
              {effectiveItems.map((item) => (
                <li key={item.id} className="summary-item">
              <div className="item-summary-content">
                {item.image && (
                  <div className="item-thumbnail">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="thumbnail-image"
                    />
                  </div>
                )}
                <div className="item-details">
                  <span className="label">{item.name}</span>
                  <span className="value">Qty: {item.quantity}</span>
                </div>
              </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="summary-section">
            <h3>Return Type</h3>
            <div className="summary-item">
              <span className="label">Type:</span>
              <span className="value capitalize">
                {effectiveReturnType === "return" && (
                  <>
                    <i className="fas fa-undo-alt mr-2"></i> Return for Refund
                  </>
                )}
                {effectiveReturnType === "exchange" && (
                  <>
                    <i className="fas fa-exchange-alt mr-2"></i> Exchange for
                    Other Items
                  </>
                )}
                {effectiveReturnType === "store-credit" && (
                  <>
                    <i className="fas fa-gift mr-2"></i> Store Credit
                  </>
                )}
              </span>
            </div>
          </div>

          {effectiveNotes && (
            <div className="summary-section">
              <h3>Notes</h3>
              <div className="notes-preview">{effectiveNotes}</div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="form-error mb-4">{error}</div>}

      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          disabled={loading}
        >
          <i className="fas fa-arrow-left mr-2"></i> Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i> Submitting...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane mr-2"></i> Submit Request
            </>
          )}
        </button>
      </div>
    </div>
  );
}
